import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import Table from 'react-bootstrap/Table';
import { Editor } from '@tinymce/tinymce-react';
import api from '../services/api.service';
import { MDPNarrativeData, MDPNarrativesResponseData } from '../types/mdp.types';
import { useStepValid } from '../hooks/useStepValid';
import { StepProps, StepWizardData } from '../types/generic.types';
import { mdpStepState } from '../services/state.service';
import AlertModal from './generic/AlertModal';
import { cleanText } from '../utils';

function MDPNarrative({
  id,
}: StepProps): JSX.Element {
  const { t } = useTranslation();
  const [MDPNarratives, setMDPNarratives] = useState<MDPNarrativeData[]>([]);
  const [, setSteps] = useRecoilState<StepWizardData[]>(mdpStepState);
  const [ref, setRef] = useState(0);
  const requiredFields = useMemo(() => [], []);
  const editorRef = useRef<any>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [validContentLen, setValidContentLen] = useState(true);

  const handleEditorChange = useCallback((content: string, key: string, narrativeId: number): void => {
    const characterLimit = 2000;
    const cleanedText = cleanText(content);
    if (cleanedText.length > characterLimit) {
      setShowAlert(true);
      setValidContentLen(false);
    } else {
      setValidContentLen(true);
    }

    setMDPNarratives(MDPNarratives.map((r: any) => ((r.id === narrativeId ? { ...r, [key]: content } : r))));
  }, [MDPNarratives]);

  useStepValid('narratives', MDPNarratives, requiredFields, mdpStepState);

  useEffect(() => {
    api.get(`/mdp/${id}/narrative/`).then(
      (response: MDPNarrativesResponseData) => {
        const sortedData = response.data.sort((a, b) => a.displayOrder - b.displayOrder);
        setMDPNarratives(sortedData);
      },
    );
  }, [id]);

  useEffect(() => {
    if (ref > 0) {
      const option = MDPNarratives.filter((e: any) => e.id === ref)[0];
      if (option.response) {
        // Strip out font-weight styling from spans
        option.response = option.response.replace(
          /(<span[^>]*?)style="[^"]*?font-weight:[^";]*;?([^"]*?)"([^>]*>)/g,
          '$1style="$2"$3',
        );
      }

      api.put(`/mdp/${id}/narrative/${ref}/`, option).then(() => {
        // todo: add saved indicator, and add exception handler
      });
    }
    setRef(0);
  }, [MDPNarratives, ref]);

  useEffect(() => {
    if (validContentLen) {
      setSteps((p: StepWizardData[]) => (
        p.map((b: StepWizardData) => {
          if (b.key === 'narratives') {
            return { ...b, isValid: true };
          }

          return b;
        })
      ));
    } else {
      setSteps((p: StepWizardData[]) => (
        p.map((b: StepWizardData) => {
          if (b.key === 'narratives') {
            return { ...b, isValid: false };
          }

          return b;
        })
      ));
    }
  }, [MDPNarratives]);

  return (
    <div className="mb-3 mx-auto mobiletable col-lg-8">
      <AlertModal
        show={showAlert}
        title={t('Character_Limit_Exceeded')}
        description={t('1500_Char_Limit_Description')}
        closeLabel={t('OK')}
        callback={() => setShowAlert(!showAlert)}
      />
      <div className="title">{t('MDP_Narratives')}</div>
      <div className="col-12 mb-3">
        {t('MDP_Narratives_Description')}
      </div>
      {
        MDPNarratives.map((option: MDPNarrativeData) => (
          <div key={`mdp-narrative-${option.id}`} className="border border-dark rounded-3 mb-3">
            <Table className="mdp-narrative-panel">
              <thead>
                <tr className="text-center fw-bold narrative-question">
                  <td>{option.question}</td>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div className="mb-1">
                      <span>Response:</span>
                    </div>
                    <Editor
                      onInit={(evt: any, editor: any): any => { editorRef.current = editor; }}
                      value={option.response || ''}
                      onEditorChange={(content) => handleEditorChange(content, 'response', option.id)}
                      init={{
                        menubar: false,
                        statusbar: false,
                        branding: false,
                        plugins: [
                          'advlist autolink lists link image charmap print preview anchor',
                          'searchreplace visualblocks code fullscreen',
                          'insertdatetime media table paste code autoresize',
                        ],
                        toolbar: 'fontselect | formatselect | '
                          + 'alignleft aligncenter alignright alignjustify |'
                          + 'bold italic strikethrough underline | forecolor backcolor link bullist numlist',
                        contextmenu: false,
                        content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                        init_instance_callback(editor: any) {
                          editor.on('blur', () => { setRef(option.id); });
                        },
                      }}
                    />
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        ))
      }
    </div>
  );
}

export default MDPNarrative;
